import { Shimmer, ShimmerElementType } from "@fluentui/react";
import { OptionOnSelectData } from "@fluentui/react-components";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logException } from "../../../../helper/AppInsightsHelper";
import CustomDropdown from "../../Common/CustomDropdown";
import { ProductFeatureChart } from "./ProductFeatureChart";
import { FeatureResponseProps } from "../../Common/ProductView.interfaces";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

export interface GranularSummaryChartProps {
  isSuccess: boolean;
  isLoading: boolean;
  isError: boolean;
  data: FeatureResponseProps | undefined;
  width: number | undefined;
  kustoLink: string | undefined;
  error: FetchBaseQueryError | SerializedError | undefined;
}

export default function GranularSummaryChart(props: GranularSummaryChartProps) {
  const [searchParams] = useSearchParams();

  const [sortedData, setSortedData] = useState<any[]>([]);
  const [aggregation, setAggregation] = useState<string>(searchParams.get("granularity") ?? "Monthly");
  const containerRef = useRef<HTMLDivElement>(null);
  const aggregationOptions = [
    { text: "Daily", value: "Daily" },
    { text: "Monthly", value: "Monthly" }
  ];

  const handleSelectedAggregation = ({ optionText }: OptionOnSelectData) => {
    setAggregation(optionText ?? aggregation);
  };

  useLayoutEffect(() => {
    const calculateResponsiveWidth = () => {
      if (containerRef.current) {
        containerRef.current.offsetWidth;
      }
    };
    calculateResponsiveWidth();
    window.addEventListener("resize", calculateResponsiveWidth);
    return () => window.removeEventListener("resize", calculateResponsiveWidth);
  }, []);

  useEffect(() => {
    if (props.isLoading) {
      setSortedData([]);
    } else if (props.isError) {
      logException("Product View Page", "Summary Tab - Features Telemetry", "useGetFeaturesDataQuery", props.error);
      setSortedData([]);
    } else if (props.data?.featuresByName) {
      const items = props.data.featuresByName || [];
      // Filter by selected aggregation type to avoid showing both daily and monthly data
      const filteredItems = items.filter(
        (item) => !item.aggregationType || item.aggregationType.toLowerCase() === aggregation.toLowerCase()
      );
      setSortedData(filteredItems);
    }
  }, [props.data, props.isLoading, props.isError, aggregation]);

  return (
    <>
      {/* Error Handling */}
      {props.isError && <DisplayAPIsException height={250} />}

      {/* No Data Found */}
      {!props.isLoading && sortedData.length === 0 && (
        <span style={{ marginLeft: "45%", marginTop: 50, background: "#FFF", lineHeight: 2 }}>
          No Feature Data found for the given Product
        </span>
      )}

      <div ref={containerRef}>
        <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8, marginLeft: 8 }}>
          {props.isLoading ? (
            <div style={{ flexBasis: "100%" }}>
              {" "}
              <Shimmer
                shimmerColors={{ shimmer: "var(--shimmer-color)" }}
                styles={{
                  root: { paddingTop: 10, paddingBottom: 10, paddingLeft: 10, width: "25%" },
                  shimmerWrapper: { ".ms-ShimmerGap-root": { background: "var(--colorBodyBackground1)" } }
                }}
                shimmerElements={[
                  { type: ShimmerElementType.line, width: "15%" },
                  { type: ShimmerElementType.gap, width: 10 },
                  { type: ShimmerElementType.line, width: "15%" }
                ]}
              />{" "}
            </div>
          ) : (
            <CustomDropdown
              label="Data Granularity"
              data={aggregationOptions}
              defaultOption={{ optionText: aggregation, optionValue: aggregation, selectedOptions: [aggregation] }}
              onSelectedOption={handleSelectedAggregation}
              styles={{ root: { minWidth: 140 } }}
            />
          )}
        </div>

        <ProductFeatureChart
          granularity={aggregation}
          isLoading={props.isLoading}
          width={props.width}
          kustoLink={props.kustoLink}
          carouseldata={{
            name: "",
            items: [
              {
                name: "Feature View - All Up",
                data: sortedData,
                kustoLink: "" // No kustoLink in this data structure
              }
            ]
          }}
        />
      </div>
    </>
  );
}
